import { datadogLogs } from "@datadog/browser-logs";
import { useAuth } from "app/Auth/AuthBoundary";
import NetworkError from "app/Auth/NetworkError";
import { LoginContainer } from "app/styles";
import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Alert,
  Heading,
  TextLink,
  useColorMode,
  VStack,
  VyLogo,
} from "shared/components/Spor";
import styled from "styled-components";

const StyledTextLink = styled(TextLink)`
  display: inline;
  margin: 0 0.5ch;
`;

const createBodyText = (
  translate: TFunction<"translation", undefined>,
  error?: any,
): string => {
  const loggerInternalContext = datadogLogs.getInternalContext();
  const sessionId = loggerInternalContext?.session_id;
  const errorText = error ? JSON.stringify(error) : "No error text found";
  const text =
    `${translate("Hei Team Brukerflater Drops")},` +
    `\n\n` +
    `(${translate("Legg inn dine detaljer her")})` +
    `\n\n` +
    `--- ${translate("Tekniske detaljer")} ---` +
    `\n\n` +
    `Datadog Session ID: ${sessionId}` +
    `\n\n` +
    `${errorText}`;
  return encodeURIComponent(text);
};

export const mailLinkWithError = (
  translate: TFunction<"translation", undefined>,
  error: any,
): string =>
  `mailto:team-brukerflater-drops@vy.no` +
  `?cc=vygruppen@service-now.com` +
  `&subject=${translate("Nettverksfeil i Dashboard")}` +
  `&body=${createBodyText(translate, error)}`;

const AuthErrorAlert = ({ error }: { error: Error | null }) => {
  const { t } = useTranslation();
  const reloadPage = () => window.location.reload();

  return (
    <Alert data-color="critical" mt="24px">
      {t("Noe gikk galt under innlogging. Forsøk å")}
      <StyledTextLink
        onClick={reloadPage}
        style={{ margin: "0 0.5ch", display: "inline" }}
      >
        {t("laste siden")}
      </StyledTextLink>
      {t("på nytt, eller")}
      <StyledTextLink
        href={mailLinkWithError(t, error)}
        target="_blank"
        rel="noopener noreferer"
        style={{ margin: "0 0.5ch", display: "inline" }}
      >
        {t("kontakt Brukerflater DROPS")}
      </StyledTextLink>
    </Alert>
  );
};

const SessionExpiredAlert = () => {
  const { t } = useTranslation();
  return (
    <Alert data-color="info" mt={4}>
      {t("Din økt har utløpt. Vennligst logg inn på nytt for å fortsette.")}
    </Alert>
  );
};

export const Login: React.FC = () => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const { signInWithRedirect, hasErrored, error, sessionExpired } = useAuth();
  const isNetworkError = error?.message === "Network Error";

  return (
    <LoginContainer>
      <VStack justify="center">
        {isNetworkError ? (
          <NetworkError error={error} />
        ) : (
          <>
            <Heading variant="xl-display" as="h1">
              {t("Drops Dashboard")}
            </Heading>
            <Button
              size="lg"
              width="180px"
              onClick={signInWithRedirect}
              data-testid="login"
            >
              {t("Logg inn")}
            </Button>
            {sessionExpired && <SessionExpiredAlert />}
            {hasErrored && <AuthErrorAlert error={error} />}
          </>
        )}
      </VStack>
      <VyLogo
        colorScheme={colorMode}
        width="100px"
        top="15px"
        left="15px"
        position="absolute"
      />
    </LoginContainer>
  );
};
